import { formatDate } from '../../../config/appConfig';
import { exportReportToExcel } from '../../../utils/reportExport';
import type { RestRecordCrew, RestRecordInterval, RestRecordSheet } from './RestRecordReport';

const DAY_MIN = 24 * 60;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatHours(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '';
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function formatMinute(m: number): string {
  const clamped = Math.max(0, Math.min(DAY_MIN, m));
  return `${pad2(Math.floor(clamped / 60))}:${pad2(clamped % 60)}`;
}

function formatWorkPeriods(intervals: RestRecordInterval[] | undefined): string {
  if (!intervals?.length) return '';
  return intervals
    .map((iv) => {
      const start = Math.max(0, iv.startMinute);
      const end = Math.min(DAY_MIN, iv.endMinute > DAY_MIN ? DAY_MIN : iv.endMinute);
      if (end <= start) return '';
      return `${formatMinute(start)}–${formatMinute(end)}`;
    })
    .filter(Boolean)
    .join(', ');
}

function crewRows(crew: RestRecordCrew, dates: string[], annotateCompliance: boolean) {
  return dates.map((date) => {
    const cell = crew.days[date];
    const nonCompliant = annotateCompliance && cell != null && cell.compliant === false;
    return [
      crew.crewName,
      crew.rankName || '',
      crew.watchkeeper ? 'Yes' : 'No',
      formatDate(date),
      formatHours(cell?.restHours),
      formatHours(cell?.workHours),
      cell
        ? `W ${formatHours(cell.workHours)} / R ${formatHours(cell.restHours)}`
        : '',
      cell
        ? `W ${formatHours(cell.workHours7d)} / R ${formatHours(cell.restHours7d)}`
        : '',
      formatWorkPeriods(cell?.intervals),
      cell?.notes?.trim() || '',
      cell == null ? '' : cell.compliant === false ? 'No' : cell.compliant === true ? 'Yes' : '',
      nonCompliant || cell?.compliant === false
        ? (cell?.violationCodes ?? []).join(', ')
        : '',
    ];
  });
}

export async function exportRestRecordToExcel(options: {
  data: RestRecordSheet;
  crewId?: number | null;
  annotateCompliance?: boolean;
}): Promise<void> {
  const { data, crewId = null, annotateCompliance = false } = options;
  const crewList =
    crewId == null ? data.crew : data.crew.filter((c) => c.crewId === crewId);

  const headers = [
    'Seafarer',
    'Rank',
    'Watchkeeper',
    'Date',
    'Hours of rest (24h)',
    'Hours of work (24h)',
    'Work / rest any 24h',
    'Work / rest any 7d',
    'Work periods',
    'Comments',
    'Compliant',
    'Violation codes',
  ];

  const rows = crewList.flatMap((c) => crewRows(c, data.dates, annotateCompliance));
  const vesselBit = (data.vesselAbrv || data.vesselName || 'vessel')
    .replace(/[^\w.-]+/g, '_')
    .slice(0, 40);
  const crewBit =
    crewId != null && crewList[0]
      ? `_${crewList[0].crewName.replace(/[^\w.-]+/g, '_').slice(0, 24)}`
      : '';

  await exportReportToExcel({
    reportTitle: `Record of hours of rest — ${data.vesselName}${data.vesselAbrv ? ` (${data.vesselAbrv})` : ''} — ${data.month}`,
    fileName: `rest-record_${vesselBit}_${data.month}${crewBit}`,
    table: { headers, rows },
    rightAlignColumnIndices: [4, 5],
  });
}
